import { cn } from "@/lib/utils";
import { SIT } from "../../lib/calc";
import { CLASS_STYLE } from "../../lib/constants";

export const inputCls = "w-full h-9 bg-[#0B1520] border border-[#1E2F45] rounded-sm px-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#FFD500] focus:ring-1 focus:ring-[#FFD500]/30 transition-colors";

export function Field({ label, hint, children, className }) {
  return (
    <label className={cn("flex flex-col gap-1.5 min-w-0", className)}>
      <span className="caption-label">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-slate-500 leading-snug">{hint}</span>}
    </label>
  );
}

export function TextInput({ value, onChange, testId, className, ...rest }) {
  return <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cn(inputCls, className)} {...rest} />;
}

export function NumInput({ value, onChange, testId, className, ...rest }) {
  return (
    <input data-testid={testId} value={value ?? ""} inputMode="decimal" onChange={(e) => onChange(e.target.value.replace(/[^0-9,.\-+]/g, ""))}
      className={cn(inputCls, "font-mono", className)} {...rest} />
  );
}

export function TextArea({ value, onChange, testId, className, rows = 3, ...rest }) {
  return <textarea data-testid={testId} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cn(inputCls, "h-auto py-2 leading-relaxed", className)} {...rest} />;
}

export function NSelect({ value, onChange, options, testId, placeholder, className }) {
  return (
    <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cn(inputCls, "pr-7", className)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export function Panel({ title, subtitle, actions, children, className, testId }) {
  return (
    <section data-testid={testId} className={cn("bg-[#0B1520] border border-[#1E2F45] rounded-sm fade-up", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center gap-3 justify-between px-4 sm:px-5 py-3 border-b border-[#1E2F45]">
          <div className="flex items-center gap-3">
            <span className="h-5 w-1 bg-[#FFD500]" />
            <div>
              <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white leading-none">{title}</h3>
              {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

export function StepTitle({ n, title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 fade-up">
      <div>
        <div className="font-mono text-xs tracking-[0.3em] text-[#FFD500]">TELA {String(n).padStart(2, "0")} / 09</div>
        <h1 className="font-display font-black uppercase tracking-wide text-3xl sm:text-4xl lg:text-5xl text-white mt-1" data-testid="step-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-2 max-w-3xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function ActionButton({ children, onClick, testId, icon: Icon, variant = "primary", disabled, className, type = "button" }) {
  const styles = {
    primary: "bg-[#FFD500] text-[#050B10] hover:bg-[#E6C000] border-[#FFD500]",
    outline: "bg-transparent text-slate-200 border-[#1E2F45] hover:border-[#FFD500]/70 hover:text-white",
    blue: "bg-[#0055A5] text-white border-[#0055A5] hover:bg-[#0067C7]",
    ghost: "bg-transparent text-slate-400 border-transparent hover:text-white",
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testId}
      className={cn("h-10 px-4 inline-flex items-center justify-center gap-2 border rounded-sm font-display font-bold tracking-wider text-[15px] uppercase transition-[background-color,border-color,color,transform] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none", styles[variant], className)}>
      {Icon && <Icon size={16} strokeWidth={2.5} />}
      {children}
    </button>
  );
}

const SIT_CLS = {
  [SIT.DENTRO]: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  [SIT.ATENCAO]: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  [SIT.FORA]: "text-red-500 border-red-500/50 bg-red-500/10",
  [SIT.SEM_TOL]: "text-slate-300 border-slate-500/40 bg-slate-500/10",
  [SIT.INSUF]: "text-slate-500 border-slate-700 bg-transparent",
};

export function SitBadge({ situacao, testId }) {
  return <span data-testid={testId} className={cn("inline-flex whitespace-nowrap px-2 py-0.5 border rounded-sm font-mono text-[10px] font-bold tracking-wide", SIT_CLS[situacao])}>{situacao}</span>;
}

export function ClassBadge({ value, large, testId }) {
  const st = CLASS_STYLE[value];
  return (
    <span data-testid={testId} className={cn("inline-flex items-center border rounded-sm font-mono font-bold tracking-wide", large ? "px-4 py-2 text-base sm:text-lg" : "px-2 py-0.5 text-[10px]", st ? st.cls : "text-slate-400 border-slate-600")}>
      {value || "SEM CLASSIFICAÇÃO"}
    </span>
  );
}

export function CalcCell({ children, danger, testId, className }) {
  return <span data-testid={testId} className={cn("font-mono text-xs italic", danger ? "text-red-500 font-bold not-italic" : "text-[#FFD500]/90", className)}>{children}</span>;
}
